import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Calendar, Trophy, TrendingUp, BarChart3, Plus } from 'lucide-react';
import { useGamesStore, useHighlightsStore, useUserStore } from '../stores/StoreProvider';
import { useNavigate } from 'react-router-dom';
import VideoPreview from '../components/VideoPreview';
import HighlightUpload from '../components/HighlightUpload';

const Season = observer(() => {
  const navigate = useNavigate();
  const gamesStore = useGamesStore();
  const highlightsStore = useHighlightsStore();
  const userStore = useUserStore();
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    gamesStore.fetchGames();
    highlightsStore.fetchHighlights();
  }, [gamesStore, highlightsStore]);

  // Get season stats
  const seasonStats = gamesStore.getSeasonStats();
  const lastSeasonStats = gamesStore.getLastSeasonStats();
  
  // Get recent games
  const recentGames = gamesStore.getRecentGames(3);

  // Get recent highlights
  const recentHighlights = highlightsStore.getRecentHighlights(5);

  const handleUploadComplete = async () => {
    setShowUploadModal(false);
    await highlightsStore.fetchHighlights();
  };

  const renderHighlight = (highlight) => (
    <VideoPreview key={highlight.id} highlight={highlight} />
  );

  const renderGame = (game) => (
    <div key={game.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500">
          {new Date(game.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </span>
        <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
          game.our_score > game.their_score 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {game.our_score > game.their_score ? 'W' : 'L'}
        </div>
      </div>
      
      <div className="flex items-center justify-between mb-3">
        {/* Your Team */}
        <div className="flex items-center space-x-2">
          {game.userOrganization?.image_url && (
            <img
              src={game.userOrganization.image_url}
              alt="Team"
              className="w-6 h-6 rounded object-contain"
            />
          )}
          <span className="text-sm font-medium text-gray-900">
            {game.userOrganization?.team_name || game.userOrganization?.name || 'Your Team'}
          </span>
        </div>
        
        {/* Score */}
        <div className="text-center">
          <span className="text-lg font-bold text-blue-600">
            {game.our_score} - {game.their_score}
          </span>
        </div>
        
        {/* Opponent Team */}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-900">
            {game.opponent?.team_name || game.opponent?.name || 'Unknown Team'}
          </span>
          {game.opponent?.image_url && (
            <img
              src={game.opponent.image_url}
              alt="Opponent"
              className="w-6 h-6 rounded object-contain"
            />
          )}
        </div>
      </div>
      
      <div className="flex justify-around pt-3 border-t border-gray-100">
        {game.user?.position === 'goalie' ? (
          // Goalie Stats
          <>
            <div className="text-center">
              <span className="text-xs text-gray-500">Saves</span>
              <p className="text-sm font-semibold text-gray-900">{game.saves || 0}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">GA</span>
              <p className="text-sm font-semibold text-gray-900">{game.goals_against || 0}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">SV%</span>
              <p className="text-sm font-semibold text-gray-900">
                {game.shots_against > 0 ? ((game.saves / game.shots_against) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </>
        ) : (
          // Skater Stats
          <>
            <div className="text-center">
              <span className="text-xs text-gray-500">Goals</span>
              <p className="text-sm font-semibold text-gray-900">{game.goals}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">Assists</span>
              <p className="text-sm font-semibold text-gray-900">{game.assists}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">Points</span>
              <p className="text-sm font-semibold text-gray-900">{game.goals + game.assists}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Season</h1>
        <p className="text-gray-600">Track your current hockey season and remember your best moments.</p>
      </div>

      {/* Your Highlights */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Your Highlights</h2>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Add</span>
            </button>
          </div>
        </div>
        <div className="p-6">
          {highlightsStore.loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading highlights...</p>
            </div>
          ) : recentHighlights.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentHighlights.map(renderHighlight)}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-2">No highlights yet</p>
              <p className="text-sm text-gray-500">Your best moments will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Current Season Stats */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Current Season Stats</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Calendar className="text-blue-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{seasonStats.totalGames}</p>
              <p className="text-sm text-gray-600">Games Played</p>
            </div>

            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Trophy className="text-green-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{seasonStats.wins}</p>
              <p className="text-sm text-gray-600">Wins</p>
            </div>

            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="text-red-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{seasonStats.losses}</p>
              <p className="text-sm text-gray-600">Losses</p>
            </div>

            {userStore.profile?.position === 'goalie' ? (
              // Goalie Stats
              <>
                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-purple-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{seasonStats.totalSaves}</p>
                  <p className="text-sm text-gray-600">Saves</p>
                </div>

                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-orange-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{seasonStats.savePercentage}%</p>
                  <p className="text-sm text-gray-600">Save %</p>
                </div>

                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-indigo-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{seasonStats.goalsAgainstAverage}</p>
                  <p className="text-sm text-gray-600">GAA</p>
                </div>
              </>
            ) : (
              // Skater Stats
              <>
                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-purple-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{seasonStats.totalGoals}</p>
                  <p className="text-sm text-gray-600">Goals</p>
                </div>

                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-orange-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{seasonStats.totalAssists}</p>
                  <p className="text-sm text-gray-600">Assists</p>
                </div>
              </>
            )}

            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="text-indigo-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{Math.round(seasonStats.winPercentage)}%</p>
              <p className="text-sm text-gray-600">Win %</p>
            </div>
          </div>
        </div>
      </div>

      {/* Last Season Stats */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Last Season Stats</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Calendar className="text-blue-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.totalGames}</p>
              <p className="text-sm text-gray-600">Games Played</p>
            </div>

            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Trophy className="text-green-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.wins}</p>
              <p className="text-sm text-gray-600">Wins</p>
            </div>

            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="text-red-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.losses}</p>
              <p className="text-sm text-gray-600">Losses</p>
            </div>

            {userStore.profile?.position === 'goalie' ? (
              // Goalie Stats
              <>
                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-purple-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.totalSaves}</p>
                  <p className="text-sm text-gray-600">Saves</p>
                </div>

                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-orange-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.savePercentage}%</p>
                  <p className="text-sm text-gray-600">Save %</p>
                </div>

                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-indigo-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.goalsAgainstAverage}</p>
                  <p className="text-sm text-gray-600">GAA</p>
                </div>
              </>
            ) : (
              // Skater Stats
              <>
                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-purple-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.totalGoals}</p>
                  <p className="text-sm text-gray-600">Goals</p>
                </div>

                <div className="card p-4 text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="text-orange-600" size={24} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{lastSeasonStats.totalAssists}</p>
                  <p className="text-sm text-gray-600">Assists</p>
                </div>
              </>
            )}

            <div className="card p-4 text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="text-indigo-600" size={24} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{Math.round(lastSeasonStats.winPercentage)}%</p>
              <p className="text-sm text-gray-600">Win %</p>
            </div>
          </div>
        </div>
      </div>

      {/* Past Games */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Past Games</h2>
            <button
              onClick={() => navigate('/app/games')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              View More
            </button>
          </div>
        </div>
        <div className="p-6">
          {recentGames.length > 0 ? (
            <div className="space-y-4">
              {recentGames.map(renderGame)}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-2">No games yet</p>
              <p className="text-sm text-gray-500">Your game results will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Highlight Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <HighlightUpload
            onUploadComplete={handleUploadComplete}
            onCancel={() => setShowUploadModal(false)}
          />
        </div>
      )}
    </div>
  );
});

export default Season; 